import fetch from 'auth/FetchInterceptor'
import tokenStorage from 'utils/tokenStorage';

const AgentService = {}

// ---- Agent Management (Spring Boot backend) ----

// Get all agents (paginated + optional search)
AgentService.getAgents = function (page = 0, size = 20, search = '') {
	const params = { page, size };
	if (search && search.trim()) {
		params.search = search.trim();
	}
	return fetch({
		url: '/agents',
		method: 'get',
		params
	})
}

// Get all agents as list (no pagination, for clients page)
AgentService.getAgentsList = function (search = '') {
	const params = {};
	if (search && search.trim()) {
		params.search = search.trim();
	}
	return fetch({
		url: '/agents/list',
		method: 'get',
		params
	})
}

// Get single agent by ID
AgentService.getAgent = function (id) {
	return fetch({
		url: `/agents/${id}`,
		method: 'get'
	})
}

// Create agent
AgentService.createAgent = function (data) {
	return fetch({
		url: '/agents',
		method: 'post',
		data: data
	})
}

// Update agent (id included in body)
AgentService.updateAgent = function (data) {
	return fetch({
		url: '/agents',
		method: 'put',
		data: data
	})
}

// Change agent activeness (active/inactive)
AgentService.changeAgentActiveness = function (agentUid, isActive) {
	return fetch({
		url: `/agents/${agentUid}/active`,
		method: 'post',
		data: {
			isActive: isActive
		}
	})
}

// Delete agent
AgentService.deleteAgent = function (id) {
	return fetch({
		url: `/agents/${id}`,
		method: 'delete'
	})
}

// Update agent status
AgentService.updateAgentStatusAdmin = function (id, status) {
	return fetch({
		url: `/agents/${id}/status`,
		method: 'post',
		data: { status }
	})
}

// Auto login as agent (Spring Boot backend)
AgentService.autoAgentLoginAdmin = function (agentId) {
	return fetch({
		url: `/agents/${agentId}/auto-login`,
		method: 'post'
	})
}

// Assign devices (with optional task each) to an agent
AgentService.assignDeviceAndTasks = function (agentId, assignments) {
	return fetch({
		url: `/agents/${agentId}/assign-device-task`,
		method: 'post',
		data: { assignments }
	})
}

// ===== AGENT PORTAL ENDPOINTS =====

// Agent login
AgentService.agentLogin = function (data) {
	return fetch({
		url: '/agent/login',
		method: 'post',
		data: data
	})
}

// Get agent profile — aucune requête HTTP si pas de token (timers refreshToken / sidebar, etc.)
AgentService.getAgentProfile = function () {
	if (!tokenStorage.getItem('user_role')) {
		return Promise.resolve({ success: false, data: null, message: 'No auth token' });
	}
	return fetch({
		url: '/agent/get_me',
		method: 'get'
	})
}

// Get agent assigned lines/devices for new chat and outbound calls
AgentService.getMyLines = function () {
	return fetch({
		url: '/agent/get_my_lines',
		method: 'get'
	})
}

// Get clients assigned to current agent (Spring backend)
AgentService.getMyClientsForAgent = function () {
	return fetch({
		url: '/clients/my_for_agent',
		method: 'get'
	})
}

// Get devices with their assigned tasks for dialer
AgentService.getDevicesWithTasks = function (clientUuid = null) {
	const params = {}
	if (clientUuid) {
		params.clientUuid = clientUuid
	}
	return fetch({
		url: '/agent/get_devices_with_tasks',
		method: 'get',
		params
	})
}

AgentService.toggleDeviceIncomingWorkflow = function (data) {
	return fetch({
		url: '/agent/toggle_device_incoming_workflow',
		method: 'post',
		data
	})
}

// Get tasks by device (filtered by deviceUuid)
AgentService.getTasksByDevice = function (deviceUuid) {
	return fetch({
		url: `/call_force/tasks/by-device/${deviceUuid}`,
		method: 'get'
	})
}

// Update agent profile (for agent portal - uses validateAgent)
AgentService.updateAgentProfile = function (data) {
	return fetch({
		url: '/call_force/agent/update_profile',
		method: 'post',
		data: data
	})
}

// Get Twilio device token for agent
AgentService.getDeviceToken = function (data) {
	return fetch({
		url: '/call_force/get_device_token',
		method: 'post',
		data: data
	})
}

// Get next call from assigned task queue (uses new Spring Boot endpoint)
AgentService.getTaskCall = function (data) {
	return fetch({
		url: '/agent/get_next_call',
		method: 'post',
		data: data
	})
}

// Update call log after call completion
AgentService.updateCallLog = function (data) {
	return fetch({
		url: '/call_force/update_call_log',
		method: 'post',
		data: data
	})
}

// Get call log list for a specific task (TaskLog list)
AgentService.getTaskLogs = function (taskId) {
	return fetch({
		url: '/call_force/get_log_by_id',
		method: 'post',
		data: { id: taskId }
	})
}

// Sync phonebook contacts to task journal (creates TaskLogs for new contacts)
AgentService.syncContacts = function (taskId) {
	return fetch({
		url: '/call_force/sync_contacts',
		method: 'post',
		data: { task_id: taskId }
	})
}

// Insert incoming call record
AgentService.insertIncomingCall = function (data) {
	return fetch({
		url: '/call_force/insert_incoming_call',
		method: 'post',
		data: data
	})
}

// Insert outgoing call record
AgentService.insertOutgoingCall = function (data) {
	return fetch({
		url: '/call_force/insert_outgoing_call',
		method: 'post',
		data: data
	})
}

// Update incoming call record
AgentService.updateIncomingCall = function (data) {
	return fetch({
		url: '/call_force/update_incoming_call',
		method: 'post',
		data: data
	})
}

// Update call status (accepted, rejected, cancelled, completed, missed).
// extra can be a number (duration) or an object { duration, call_from, call_to, deviceId, deviceTitle, deviceNumber, contactName, agent_comments }.
AgentService.updateCallStatus = function (sid, status, call_type, durationOrExtra) {
	const data = { sid, status, call_type };
	if (typeof durationOrExtra === 'number') {
		data.duration = durationOrExtra;
	} else if (durationOrExtra && typeof durationOrExtra === 'object') {
		if (durationOrExtra.duration != null) data.duration = durationOrExtra.duration;
		if (durationOrExtra.call_from != null) data.call_from = durationOrExtra.call_from;
		if (durationOrExtra.call_to != null) data.call_to = durationOrExtra.call_to;
		if (durationOrExtra.deviceId != null) data.deviceId = durationOrExtra.deviceId;
		if (durationOrExtra.deviceTitle != null) data.deviceTitle = durationOrExtra.deviceTitle;
		if (durationOrExtra.deviceNumber != null) data.deviceNumber = durationOrExtra.deviceNumber;
		if (durationOrExtra.contactName != null) data.contactName = durationOrExtra.contactName;
		if (durationOrExtra.agent_comments != null) data.agent_comments = durationOrExtra.agent_comments;
		if (durationOrExtra.goal_achieved != null) data.goal_achieved = durationOrExtra.goal_achieved;
	}
	return fetch({
		url: '/call_force/update_call_status',
		method: 'post',
		data
	})
}

// Historique / liste : data = tableau d'appels, pagination = meta (inchangé pour call-history).
// Dashboard stats : supplementalStatsRows en plus (task logs), ignoré par call-history.
AgentService.getIncomingCalls = async function (params = {}) {
	const response = await fetch({
		url: '/call_history/my_incoming_calls',
		method: 'get',
		params: params // { page, limit, sortBy, search }
	})

	if (response?.success && response?.data && Array.isArray(response.data.content)) {
		const page = response.data
		return {
			success: true,
			data: page.content,
			pagination: page,
			supplementalStatsRows: page.supplementalStatsRows || [],
			counts: page.counts != null ? page.counts : null,
			msg: response.msg
		}
	}

	// PageResponse Spring brut (pas de wrapper ApiResponse)
	if (response && Array.isArray(response.content)) {
		return {
			success: true,
			data: response.content,
			pagination: response,
			supplementalStatsRows: response.supplementalStatsRows || [],
			counts: response.counts != null ? response.counts : null,
			msg: response.msg
		}
	}

	return response
}

// Get contacts from assigned tasks
AgentService.getContacts = function () {
	return fetch({
		url: '/call_force/get_my_contacts',
		method: 'get'
	})
}

// QUEUE SYSTEM: Update agent status (available/busy/offline/break/lunch/meeting)
AgentService.updateAgentStatus = async function (status, reason = null) {
	// First get agent profile to get agent ID
	const profileResponse = await AgentService.getAgentProfile();
	if (!profileResponse || !profileResponse.success || !profileResponse.data) {
		throw new Error('Failed to get agent profile');
	}
	const agentId = profileResponse.data.uid || profileResponse.data.agent_id;
	if (!agentId) {
		throw new Error('Agent ID not found in profile');
	}
	
	// Map frontend status values to backend enum values
	const statusMap = {
		'available': 'available',
		'busy': 'in_call',
		'break': 'on_break',
		'lunch': 'at_lunch',
		'meeting': 'in_meeting',
		'offline': 'offline'
	};
	
	const backendStatus = statusMap[status] || status;
	
	// Use new API endpoint
	const response = await fetch({
		url: `/agents/${agentId}/status`,
		method: 'post',
		data: { status: backendStatus, reason }
	});
	
	// Map backend status values to frontend status values
	const reverseStatusMap = {
		'available': 'available',
		'in_call': 'busy',
		'busy': 'busy',
		'on_break': 'break',
		'at_lunch': 'lunch',
		'in_meeting': 'meeting',
		'offline': 'offline'
	};
	
	// Normalize response - backend returns agent object directly
	let normalizedResponse = response;
	
	// If response is the agent object directly (has id/email and status, but no success field)
	if (response && (response.id || response.email) && response.status && !response.success) {
		const frontendStatus = reverseStatusMap[response.status] || response.status;
		normalizedResponse = {
			success: true,
			data: {
				...response,
				status: frontendStatus
			}
		};
	}
	// If response has data property
	else if (response && response.data) {
		// Map status if it exists
		if (response.data.status && reverseStatusMap[response.data.status]) {
			response.data.status = reverseStatusMap[response.data.status];
		}
		// Ensure success field exists
		normalizedResponse = {
			...response,
			success: response.success !== undefined ? response.success : true
		};
	}
	// If response already has success field
	else if (response && response.success) {
		// Map status if it exists directly in response
		if (response.status && reverseStatusMap[response.status] && !response.data) {
			normalizedResponse = {
				success: true,
				data: {
					...response,
					status: reverseStatusMap[response.status]
				}
			};
		} else {
			normalizedResponse = response;
		}
	}
	// Default: treat as success if we got a response
	else if (response) {
		normalizedResponse = {
			success: true,
			data: response
		};
	} else {
		normalizedResponse = {
			success: false,
			data: null
		};
	}
	
	return normalizedResponse;
}

// QUEUE SYSTEM: Register agent to device
AgentService.registerAgentDevice = function (device_id) {
	return fetch({
		url: '/call_force/agent/register_device',
		method: 'post',
		data: { device_id }
	})
}

// Get agent's own status logs
AgentService.getAgentStatusLogs = function () {
	return fetch({
		url: '/call_force/agent/status_logs',
		method: 'get'
	})
}

// USER: Update agent status (now uses Spring Boot endpoint)
AgentService.updateAgentStatusUser = function (agentId, status, reason = null) {
	return fetch({
		url: `/agents/${agentId}/status`,
		method: 'post',
		data: { status, reason }
	})
}

// USER: Get agent status logs (will be migrated to Spring Boot when endpoint exists)
AgentService.getAgentStatusLogsUser = function (agentId = null) {
	const url = agentId
		? `/agent/agent_status_logs/${agentId}`
		: '/agent/agent_status_logs';
	return fetch({
		url: url,
		method: 'get'
	})
}

// ===== AGENT LEADS MANAGEMENT =====

// Create new lead
AgentService.createLead = function (data) {
	return fetch({
		url: '/agent/create_lead',
		method: 'post',
		data: data
	})
}

// Get all leads (optional filters: is_teammate, search, page, limit)
AgentService.getLeads = function (isTeammate = null, options = {}) {
	const params = {};
	if (isTeammate !== null && isTeammate !== undefined) {
		params.is_teammate = isTeammate;
	}
	if (options.search !== undefined && options.search !== null && String(options.search).trim() !== '') {
		params.search = String(options.search).trim();
	}
	if (options.page !== undefined && options.page !== null) {
		params.page = options.page;
	}
	if (options.limit !== undefined && options.limit !== null) {
		params.limit = options.limit;
	}
	return fetch({
		url: '/agent/get_leads',
		method: 'get',
		params: params
	})
}

// Get single lead
AgentService.getLead = function (uid) {
	return fetch({
		url: '/agent/get_lead',
		method: 'post',
		data: { uid }
	})
}

// Update lead
AgentService.updateLead = function (data) {
	return fetch({
		url: '/agent/update_lead',
		method: 'post',
		data: data
	})
}

// Delete lead
AgentService.deleteLead = function (uid) {
	return fetch({
		url: '/agent/delete_lead',
		method: 'post',
		data: { uid }
	})
}

// Transfer call to another number
AgentService.transferCall = function (data) {
	return fetch({
		url: '/call_force/transfer_call',
		method: 'post',
		data: data
	});
}

export default AgentService
